// Experimental geometry proposals. No artwork, density or machine settings are mutated.
const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
const area = (p) =>
  p.reduce((s, a, i) => {
    const b = p[(i + 1) % p.length];
    return s + a.x * b.y - b.x * a.y;
  }, 0) / 2;
const arc = (p, a, b) => {
  const out = [p[a]];
  while (a !== b) {
    a = (a + 1) % p.length;
    out.push(p[a]);
  }
  return out;
};
export function inside(point, polygon) {
  let hit = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[i],
      b = polygon[j];
    const dx = b.x - a.x,
      dy = b.y - a.y;
    const len2 = dx * dx + dy * dy;
    const cross = (point.x - a.x) * dy - (point.y - a.y) * dx;
    const dot = (point.x - a.x) * dx + (point.y - a.y) * dy;
    if (len2 > 0 && cross * cross <= len2 * 1e-14 && dot >= 0 && dot <= len2)
      return true;
    if (
      a.y > point.y !== b.y > point.y &&
      point.x < ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y) + a.x
    )
      hit = !hit;
  }
  return hit;
}
export function resample(points, count, closed = false) {
  const p = closed ? [...points, points[0]] : points;
  const lengths = [0];
  for (let i = 1; i < p.length; i++)
    lengths.push(lengths[i - 1] + dist(p[i - 1], p[i]));
  const total = lengths[lengths.length - 1];
  if (!total) return [];
  let j = 1;
  const positions = Array.from(
    { length: count },
    (_, i) => (total * i) / (closed ? count : count - 1)
  );
  if (closed) positions.push(...lengths.slice(0, -1));
  positions.sort((a, b) => a - b);
  return positions
    .filter((s, i) => !i || s - positions[i - 1] > total * 1e-8)
    .map((s) => {
      while (j < lengths.length - 1 && lengths[j] < s) j++;
      const t = (s - lengths[j - 1]) / (lengths[j] - lengths[j - 1] || 1);
      return {
        x: p[j - 1].x + (p[j].x - p[j - 1].x) * t,
        y: p[j - 1].y + (p[j].y - p[j - 1].y) * t,
      };
    });
}
const d = (points, closed = false) =>
  "M" +
  points.map((p) => `${p.x.toFixed(8)},${p.y.toFixed(8)}`).join(" L") +
  (closed ? " Z" : "");
function assess(a, b, contains) {
  const aa = resample(a, 25),
    bb = resample(b, 25);
  if (!aa.length || !bb.length) return null;
  const widths = [],
    rungs = [];
  for (let i = 1; i < 24; i++) {
    const x = aa[i],
      y = bb[i];
    for (let k = 1; k < 12; k++) {
      const t = k / 12;
      if (!contains({ x: x.x + (y.x - x.x) * t, y: x.y + (y.y - x.y) * t }))
        return null;
    }
    widths.push(dist(x, y));
    rungs.push(d([x, y]));
  }
  const mean = widths.reduce((s, w) => s + w, 0) / widths.length;
  return {
    rail0: d(a),
    rail1: d(b),
    rungs,
    mean,
    max: Math.max(...widths),
    min: Math.min(...widths),
  };
}
// A partition cut is an end cap, not a new place to choose a random split.
// Anchor each child column at the midpoint of every cut on its boundary.
function anchoredBoundary(p, cuts) {
  const onSegment = (q, a, b) => {
    const len = dist(a, b);
    return len > 1e-8 && Math.abs(dist(a, q) + dist(q, b) - len) < len * 1e-8;
  };
  const anchors = [];
  for (const [a, b] of cuts) {
    const points = p.filter((q) => onSegment(q, a, b));
    if (points.length < 2) continue;
    points.sort((x, y) => dist(a, x) - dist(a, y));
    const first = points[0],
      last = points[points.length - 1];
    if (dist(first, last) < dist(a, b) * 1e-6) continue;
    const midpoint = { x: (first.x + last.x) / 2, y: (first.y + last.y) / 2 };
    const edge = p.findIndex((q, i) =>
      onSegment(midpoint, q, p[(i + 1) % p.length])
    );
    if (edge < 0) continue;
    const existing = p.find((q) => dist(q, midpoint) < 1e-7);
    if (existing) anchors.push(existing);
    else {
      p = [...p.slice(0, edge + 1), midpoint, ...p.slice(edge + 1)];
      anchors.push(midpoint);
    }
  }
  return { p, anchors: [...new Set(anchors.map((q) => p.indexOf(q)))] };
}
function fit(p, resolution = 20, cuts = []) {
  const boundary = anchoredBoundary(p, cuts);
  p = boundary.p;
  const anchors = boundary.anchors;
  if (anchors.length > 2) return null;
  let best = null;
  const step = Math.max(1, Math.ceil(p.length / resolution)),
    gap = Math.ceil(p.length * 0.08);
  const choices =
    anchors.length === 2
      ? [anchors]
      : anchors.length === 1
        ? Array.from({ length: p.length }, (_, i) => [anchors[0], i]).filter(
            ([, i]) => i % step === 0
          )
        : Array.from(
            { length: Math.ceil(p.length / step) },
            (_, i) => i * step
          ).flatMap((a) =>
            Array.from(
              { length: Math.max(0, Math.ceil((p.length - a - gap) / step)) },
              (_, i) => [a, a + gap + i * step]
            )
          );
  for (let [a, b] of choices) {
    if (a > b) [a, b] = [b, a];
    if (b - a < 2 || p.length - b + a < 2) continue;
    const candidate = assess(arc(p, a, b), arc(p, b, a).reverse(), (q) =>
      inside(q, p)
    );
    if (candidate && (!best || candidate.mean < best.mean)) best = candidate;
  }
  return best || (resolution === 20 ? fit(p, 60, cuts) : null);
}
function reflexes(p) {
  const sign = Math.sign(area(p));
  return p
    .map((b, i) => {
      const a = p[(i + p.length - 2) % p.length],
        c = p[(i + 2) % p.length];
      const cross = (b.x - a.x) * (c.y - b.y) - (b.y - a.y) * (c.x - b.x);
      return { i, bend: (cross * sign) / (dist(a, b) * dist(b, c) || 1) };
    })
    .filter((x) => x.bend < -0.28)
    .sort((a, b) => a.bend - b.bend)
    .slice(0, 8);
}
function internal(a, b, contains) {
  return Array.from({ length: 19 }, (_, i) => (i + 1) / 20).every((t) =>
    contains({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t })
  );
}
function solve(p, depth = 0, budget = { remaining: 160 }, caps = []) {
  if (--budget.remaining < 0) return null;
  const anchored = anchoredBoundary(p, caps);
  p = anchored.p;
  const direct = fit(p, 20, caps),
    reflex = reflexes(p);
  // Prefer a single band when its width is reasonably consistent.
  if (direct && !reflex.length) return { pairs: [direct], cuts: [] };
  if (depth >= 4) return direct ? { pairs: [direct], cuts: [] } : null;
  const candidates = [];
  for (const { i } of [...reflex, ...anchored.anchors.map((i) => ({ i }))])
    for (const j of [
      ...new Set([
        ...reflex.map((r) => r.i),
        ...Array.from(
          {
            length: Math.ceil(p.length / Math.max(1, Math.ceil(p.length / 32))),
          },
          (_, k) => k * Math.max(1, Math.ceil(p.length / 32))
        ),
      ]),
    ]) {
      const gap = Math.min(Math.abs(i - j), p.length - Math.abs(i - j));
      if (gap < 6 || !internal(p[i], p[j], (q) => inside(q, p))) continue;
      const a = arc(p, i, j),
        b = arc(p, j, i);
      if (
        Math.min(Math.abs(area(a)), Math.abs(area(b))) <
        Math.abs(area(p)) * 0.06
      )
        continue;
      candidates.push({ a, b, cut: [p[i], p[j]], length: dist(p[i], p[j]) });
    }
  candidates.sort((a, b) => a.length - b.length);
  for (const c of candidates.slice(0, 16)) {
    const a = solve(resample(c.a, 48, true), depth + 1, budget, [
        ...caps,
        c.cut,
      ]),
      b = solve(resample(c.b, 48, true), depth + 1, budget, [...caps, c.cut]);
    if (a && b)
      return {
        pairs: [...a.pairs, ...b.pairs],
        cuts: [c.cut, ...a.cuts, ...b.cuts],
      };
  }
  return direct ? { pairs: [direct], cuts: [] } : null;
}
function solveHole(outer, inner) {
  if (Math.sign(area(outer)) !== Math.sign(area(inner)))
    inner = inner.slice().reverse();
  const contains = (q) => inside(q, outer) && !inside(q, inner);
  let ring = null;
  for (
    let offset = 0;
    offset < inner.length;
    offset += Math.max(1, Math.ceil(inner.length / 32))
  ) {
    const rotated = [...inner.slice(offset), ...inner.slice(0, offset)];
    const candidate = assess(
      [...outer, outer[0]],
      [...rotated, rotated[0]],
      contains
    );
    if (
      candidate &&
      candidate.max < candidate.mean * 2.1 &&
      candidate.max < candidate.min * 4 &&
      (!ring || candidate.mean < ring.mean)
    )
      ring = candidate;
  }
  if (ring)
    return { pairs: [{ ...ring, method: "existing-contours" }], cuts: [] };
  // Two short bridges open a counter into two bands; recursive cuts handle branches.
  const bridges = [];
  for (
    let i = 0;
    i < inner.length;
    i += Math.max(1, Math.ceil(inner.length / 16))
  ) {
    const nearest = outer
      .map((p, j) => ({ j, length: dist(p, inner[i]) }))
      .sort((a, b) => a.length - b.length);
    const hit = nearest.find(({ j }) => internal(inner[i], outer[j], contains));
    if (hit) bridges.push({ i, j: hit.j, length: hit.length });
  }
  bridges.sort((a, b) => a.length - b.length);
  for (let a = 0; a < Math.min(8, bridges.length); a++)
    for (let b = a + 1; b < Math.min(8, bridges.length); b++) {
      const x = bridges[a],
        y = bridges[b];
      if (
        Math.min(Math.abs(x.i - y.i), inner.length - Math.abs(x.i - y.i)) < 8 ||
        Math.min(Math.abs(x.j - y.j), outer.length - Math.abs(x.j - y.j)) < 8
      )
        continue;
      const p = [...arc(outer, x.j, y.j), ...arc(inner, x.i, y.i).reverse()];
      const q = [...arc(outer, y.j, x.j), ...arc(inner, y.i, x.i).reverse()];
      const expected = Math.abs(area(outer)) - Math.abs(area(inner));
      if (
        Math.abs(Math.abs(area(p)) + Math.abs(area(q)) - expected) >
        expected * 0.001
      )
        continue;
      const caps = [
        [outer[x.j], inner[x.i]],
        [outer[y.j], inner[y.i]],
      ];
      const left = solve(resample(p, 48, true), 0, { remaining: 160 }, caps),
        right = solve(resample(q, 48, true), 0, { remaining: 160 }, caps);
      if (left && right)
        return {
          pairs: [...left.pairs, ...right.pairs],
          cuts: [
            [outer[x.j], inner[x.i]],
            [outer[y.j], inner[y.i]],
            ...left.cuts,
            ...right.cuts,
          ],
        };
    }
  return null;
}
export async function proposeAutoSatin(
  contours,
  { cancelled = () => false, yieldControl = () => Promise.resolve() } = {}
) {
  if (!contours.length || contours.length > 48)
    throw new Error("Select a layer with 1–48 closed contours.");
  if (
    contours.some(
      (p) =>
        !Array.isArray(p) ||
        p.length < 3 ||
        p.length > 2000 ||
        p.some((q) => !Number.isFinite(q.x) || !Number.isFinite(q.y))
    )
  )
    throw new Error("This layer has invalid or overly detailed contours.");
  const rings = contours.map((p) => resample(p, 48, true));
  if (rings.some((p) => p.length < 3 || Math.abs(area(p)) < 1e-8))
    throw new Error("This layer has a degenerate outline.");
  const parents = rings.map(
    (p, i) =>
      rings
        .map((q, j) => ({ j, size: Math.abs(area(q)) }))
        .filter(
          (x) =>
            x.j !== i && x.size > Math.abs(area(p)) && inside(p[0], rings[x.j])
        )
        .sort((a, b) => a.size - b.size)[0]?.j ?? -1
  );
  const pairs = [],
    cuts = [];
  let unresolved = 0;
  for (let i = 0; i < rings.length; i++) {
    let depth = 0,
      parent = parents[i];
    while (parent !== -1) {
      depth++;
      parent = parents[parent];
    }
    if (depth % 2) continue;
    await yieldControl();
    if (cancelled()) return null;
    const holes = rings.filter((_, j) => parents[j] === i);
    const result =
      holes.length === 0
        ? solve(rings[i])
        : holes.length === 1
          ? solveHole(rings[i], holes[0])
          : null;
    if (!result) {
      unresolved++;
      continue;
    }
    // Final containment uses the original input contours, not simplified candidate pieces.
    const valid = result.pairs.every((p) =>
      assess(
        parsePolyline(p.rail0),
        parsePolyline(p.rail1),
        (q) => inside(q, contours[i]) && !holes.some((h) => inside(q, h))
      )
    );
    if (!valid) {
      unresolved++;
      continue;
    }
    pairs.push(
      ...result.pairs.map(({ rail0, rail1, rungs, method }) => ({
        rail0,
        rail1,
        rungs,
        method: method || "auto-split",
      }))
    );
    cuts.push(...result.cuts);
  }
  return {
    pairs,
    cuts: cuts.map((p) => d(p)),
    unresolved,
    complete: unresolved === 0 && pairs.length > 0,
  };
}
export const parsePolyline = (path) =>
  Array.from(path.matchAll(/([\d.eE+-]+),([\d.eE+-]+)/g), (m) => ({
    x: +m[1],
    y: +m[2],
  }));
